import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { useCachedState } from "@/lib/swr";
import { useDash } from "./ctx";
import {
  ClipboardList, Search, X, ChevronLeft, ChevronRight, MapPin,
} from "lucide-react";

// iter198m: portfolio-level view of REQUESTS across every community the caller manages —
// one queue. Replaces the previous "Portfolio Inbox" that dumped conversations. The
// original page had a Requests tab hiding behind a Conversations tab that always
// opened first, so managers rarely saw the request queue and complained the inbox
// was "just a bunch of escalated conversations." Now it opens straight to requests
// and the conversations tab is gone. Deep-links each row into the community's
// Actions page so the manager keeps the full ticket context (thread, timeline, notes).

const PRIO_STYLE = {
  urgent: "bg-red-100 text-red-700",
  high: "bg-amber-100 text-amber-700",
  normal: "bg-sand-100 text-ink/60",
  low: "bg-sand-100 text-ink/40",
};
const REQ_STATUS_STYLE = {
  open: "bg-blue-50 text-blue-700",
  in_progress: "bg-violet-50 text-violet-700",
  on_hold: "bg-amber-50 text-amber-700",
  resolved: "bg-emerald-50 text-emerald-700",
};
const REQ_STATUS_LABEL = {
  open: "Open", in_progress: "In process", on_hold: "On hold",
  resolved: "Completed", closed: "Closed",
};

const ago = (iso) => {
  if (!iso) return "";
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h`;
  return `${Math.floor(mins / 1440)}d`;
};

/* Horizontal community filter chips with per-community counts. Urgent count surfaced
   as an amber badge so managers can spot the community that needs their attention. */
function CommunityChips({ communities, value, onChange }) {
  if (!communities?.length) return null;
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mb-1" data-testid="req-community-chips">
      <button type="button" data-testid="req-community-all" onClick={() => onChange("")}
        className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${!value ? "bg-ink text-white border-ink" : "bg-white border-sand-200 hover:border-ink"}`}>
        All communities
      </button>
      {communities.map((c) => (
        <button key={c.tenant_id} type="button" data-testid={`req-community-${c.tenant_id}`}
          onClick={() => onChange(value === c.tenant_id ? "" : c.tenant_id)}
          className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${value === c.tenant_id ? "bg-ink text-white border-ink" : "bg-white border-sand-200 hover:border-ink"}`}>
          {c.name}
          <span className={`tabular-nums ${value === c.tenant_id ? "text-white/70" : "text-ink/40"}`}>{c.count}</span>
          {c.urgent > 0 && <span className="h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{c.urgent}</span>}
        </button>
      ))}
    </div>
  );
}

export default function PortfolioRequests() {
  const { viewCompanyId, enterCommunity } = useDash();
  const cq = viewCompanyId ? `&company_id=${viewCompanyId}` : "";

  const [status, setStatus] = useState("open");
  const [communityF, setCommunityF] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 30;
  const key = `pfreqs:${cq}:${status}:${communityF}:${q}:${page}`;
  const [reqs, setReqs] = useCachedState(`${key}:items`, []);
  const [total, setTotal] = useCachedState(`${key}:total`, 0);
  const [communities, setCommunities] = useCachedState(`pfreqs-comms:${cq}`, []);

  const load = useCallback(() => {
    const params = `?status=${status}&community=${communityF}&q=${encodeURIComponent(q)}&page=${page}&page_size=${pageSize}${cq}`;
    return api.get(`/portfolio/requests${params}`).then((r) => {
      setReqs(r.data.items);
      setTotal(r.data.total);
      setCommunities(r.data.communities);
    }).catch(() => {});
  }, [status, communityF, q, page, cq]);
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);
  useEffect(() => { setPage(1); }, [status, communityF, q]);
  // Keep the queue fresh so new urgent requests surface without a manual refresh.
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const urgentTotal = communities.reduce((s, c) => s + (c.urgent || 0), 0);
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-5">
      <div>
        <PageTitle title={<>Portfolio Requests
          {urgentTotal > 0 && (
            <span data-testid="portfolio-requests-urgent-badge" className="text-xs font-bold rounded-full bg-red-500 text-white px-2.5 py-1">
              {urgentTotal} urgent
            </span>
          )}</>} about="Every open request across your communities — one queue." />
      </div>

      <div className="space-y-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          {[["open", "Open"], ["resolved", "Completed"], ["", "All"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`req-status-${v || "all"}`} onClick={() => setStatus(v)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${status === v ? "bg-ink text-white border-ink" : "bg-white border-sand-200 hover:border-ink"}`}>{l}</button>
          ))}
          <div className="relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-sand-400" />
            <input data-testid="req-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title, resident or unit…"
              className="rounded-full border border-sand-200 bg-white pr-8 py-1.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 w-56 pl-9" />
            {q && <button onClick={() => setQ("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sand-400 hover:text-ink" data-testid="req-search-clear"><X className="h-3.5 w-3.5" /></button>}
          </div>
        </div>
        <CommunityChips communities={communities} value={communityF} onChange={setCommunityF} />
      </div>

      <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden" data-testid="portfolio-requests-list">
        <div className="hidden sm:grid grid-cols-[1.7fr_1fr_auto_auto_auto] gap-3 px-5 py-3 border-b border-sand-200 text-xs uppercase tracking-wider font-bold text-muted-foreground">
          <div>Request</div><div>Community</div><div>Priority</div><div>Status</div><div className="text-right">Age</div>
        </div>
        {reqs.length === 0 && (
          <div className="p-10 text-center text-muted-foreground text-sm flex flex-col items-center gap-2">
            <ClipboardList className="h-7 w-7 text-sand-300" />
            {q || communityF ? "No requests match these filters." : "No open requests — nice and quiet."}
          </div>
        )}
        {reqs.map((a) => {
          const prio = PRIO_STYLE[a.priority] ? a.priority : "normal";
          return (
            <button key={a.id} data-testid={`portfolio-req-${a.id}`}
              onClick={() => enterCommunity(a.tenant_id, `/dashboard/actions?ticket=${a.id}`)}
              title="Opens this request inside its community"
              className="w-full text-left flex flex-col gap-1.5 sm:grid sm:grid-cols-[1.7fr_1fr_auto_auto_auto] sm:gap-3 sm:items-center px-5 py-3 border-b border-sand-100 last:border-0 hover:bg-sand-50/70 transition-colors">
              <div className="min-w-0">
                <div className="font-semibold text-sm text-ink truncate">{a.title || a.summary || "Request"}</div>
                <div className="text-xs text-muted-foreground truncate">{a.resident || a.phone || ""}{homeLabel(a) ? ` · ${homeLabel(a)}` : ""}</div>
              </div>
              <div className="text-xs text-ink/60 font-semibold inline-flex items-center gap-1 min-w-0"><MapPin className="h-3 w-3 shrink-0" strokeWidth={1.75} /><span className="truncate">{a.tenant_name}</span></div>
              <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 w-fit ${PRIO_STYLE[prio]}`}>{prio}</span>
              <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 w-fit ${REQ_STATUS_STYLE[a.status] || "bg-sand-100 text-ink/50"}`}>{REQ_STATUS_LABEL[a.status] || (a.status || "").replace("_", " ")}</span>
              <span className="text-xs text-muted-foreground sm:text-right tabular-nums">{ago(a.created_at)}</span>
            </button>
          );
        })}
        {total > pageSize && (
          <div className="flex items-center justify-between px-5 py-2.5 border-t border-sand-100" data-testid="req-pagination">
            <span className="text-[11px] text-muted-foreground">{total} requests</span>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="h-7 w-7 rounded-lg border border-sand-200 flex items-center justify-center hover:border-ink disabled:opacity-40" data-testid="req-page-prev"><ChevronLeft className="h-3.5 w-3.5" /></button>
              <span className="text-xs font-semibold px-1.5 tabular-nums">{page}/{pages}</span>
              <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="h-7 w-7 rounded-lg border border-sand-200 flex items-center justify-center hover:border-ink disabled:opacity-40" data-testid="req-page-next"><ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
